/**
 * useDataTable Hook
 * Combines data fetching with table features (search, filter, sort, pagination)
 *
 * This is the recommended hook for admin list pages.
 * It eliminates repetitive data fetching patterns across pages.
 *
 * Architecture: Composes useFetch + useTableData for DRY code
 *
 * Usage:
 * const {
 *     data,
 *     isLoading,
 *     error,
 *     refetch,
 *     search,
 *     setSearch,
 *     paginatedItems,
 *     pagination,
 *     isEmpty,
 * } = useDataTable({
 *     fetcher: () => contactService.getContacts(),
 *     searchFields: ['first_name', 'last_name', 'email'],
 *     perPage: 10,
 * });
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { handleApiError } from '@/utils/errorHandler';
import { useTableData, type UseTableDataOptions, type UseTableDataReturn } from './useTableData';
import { DEFAULT_ITEMS_PER_PAGE } from '@/config/constants';

interface UseDataTableOptions<T, TRaw = T> extends Omit<UseTableDataOptions<T>, 'perPage'> {
    /** Data fetcher function */
    fetcher: () => Promise<TRaw[]>;
    /** Transform raw data after fetching */
    transform?: (data: TRaw[]) => T[];
    /** Items per page */
    perPage?: number;
    /** Action name for error messages */
    actionName?: string;
    /** Fetch immediately on mount */
    immediate?: boolean;
    /** Dependencies that trigger refetch */
    deps?: unknown[];
}

interface UseDataTableReturn<T> extends UseTableDataReturn<T> {
    // Data fetching
    data: T[];
    isLoading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    setData: React.Dispatch<React.SetStateAction<T[]>>;
    hasData: boolean;
}

export function useDataTable<T, TRaw = T>(
    options: UseDataTableOptions<T, TRaw>
): UseDataTableReturn<T> {
    const {
        fetcher,
        transform,
        actionName = 'fetch data',
        immediate = true,
        deps = [],
        perPage = DEFAULT_ITEMS_PER_PAGE,
        ...tableOptions
    } = options;

    // Data fetching state
    const [data, setData] = useState<T[]>([]);
    const [isLoading, setIsLoading] = useState(immediate);
    const [error, setError] = useState<string | null>(null);

    const mountedRef = useRef(true);
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    // Fetch data
    const refetch = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const rawResult = await fetcherRef.current();

            if (mountedRef.current) {
                const transformedData = transform ? transform(rawResult) : (rawResult as unknown as T[]);
                setData(transformedData);
            }
        } catch (err) {
            if (mountedRef.current) {
                const errorMessage = handleApiError(err, actionName, { showToast: true });
                setError(errorMessage);
            }
        } finally {
            if (mountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [actionName, transform]);

    // Mount effect
    useEffect(() => {
        mountedRef.current = true;
        if (immediate) {
            refetch();
        }
        return () => {
            mountedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    // Use useTableData for table features (search, sort, filter, pagination)
    const tableData = useTableData(data, {
        ...tableOptions,
        perPage,
    });

    return {
        // Data fetching
        data,
        isLoading,
        error,
        refetch,
        setData,
        hasData: data.length > 0,

        // Table features from useTableData
        ...tableData,

        // Override isEmpty to account for loading state
        isEmpty: tableData.paginatedItems.length === 0 && !isLoading,
    };
}

export default useDataTable;
